function asyncGeneratorStep(n, t, e, r, o, a, c) { try { var i = n[a](c), u = i.value; } catch (n) { return void e(n); } i.done ? t(u) : Promise.resolve(u).then(r, o); }
function _asyncToGenerator(n) { return function () { var t = this, e = arguments; return new Promise(function (r, o) { var a = n.apply(t, e); function _next(n) { asyncGeneratorStep(a, r, o, _next, _throw, "next", n); } function _throw(n) { asyncGeneratorStep(a, r, o, _next, _throw, "throw", n); } _next(void 0); }); }; }
/**
 * createAlgoliaPlp — factory that wires client + state + search into a
 * single event-driven PLP controller.
 *
 * Usage:
 *   const plp = createAlgoliaPlp({ ... });
 *   plp.on('results', ({ state, hits, facets, nbHits, nbPages }) => { ... });
 *   plp.search();
 */

import { ALGOLIA_PRICE_ATTRIBUTE } from './constants';
import { getAlgoliaClient } from './algolia-client';
import { AlgoliaState } from './algolia-state';
import { buildSearchParams, executeSearch } from './algolia-search';
var SEARCH_DEBOUNCE_MS = 16;
class AlgoliaPlp {
  /**
   * @param {object} opts
   * @param {string} opts.appId
   * @param {string} opts.searchApiKey
   * @param {string} opts.indexName
   * @param {Array}  opts.facets
   * @param {Array}  opts.sortReplicas
   * @param {number} opts.priceScale
   * @param {number} opts.hitsPerPage
   * @param {string[]} opts.collectionFacetFilters
   * @param {string|null} [opts.separateCardAttr]
   * @param {boolean} opts.urlSync
   * @param {ReturnType<import('./parse-default-filters').normalizePreselectedFilters> | null} [opts.defaultFilters]
   */
  constructor(opts) {
    var _opts$priceScale, _opts$urlSync, _opts$defaultFilters;
    this._client = getAlgoliaClient({
      appId: opts.appId,
      searchApiKey: opts.searchApiKey
    });
    this._searchConfig = {
      indexName: opts.indexName,
      hitsPerPage: opts.hitsPerPage || 12,
      priceAttribute: ALGOLIA_PRICE_ATTRIBUTE,
      priceScale: (_opts$priceScale = opts.priceScale) !== null && _opts$priceScale !== void 0 ? _opts$priceScale : 100,
      collectionFacetFilters: opts.collectionFacetFilters || [],
      facets: opts.facets || [],
      separateCardAttr: opts.separateCardAttr || null
    };
    this.state = new AlgoliaState({
      facets: opts.facets || [],
      sortReplicas: opts.sortReplicas || [],
      defaultSortIndex: opts.indexName,
      urlSync: (_opts$urlSync = opts.urlSync) !== null && _opts$urlSync !== void 0 ? _opts$urlSync : false,
      defaultFilters: (_opts$defaultFilters = opts.defaultFilters) !== null && _opts$defaultFilters !== void 0 ? _opts$defaultFilters : null
    });
    this._listeners = {};
    this._debounceTimer = null;
    this.state.on('stateChange', () => this._debouncedSearch());
  }

  // -- EventEmitter pass-through ------------------------------------------

  on(event, fn) {
    var _this$_listeners;
    ((_this$_listeners = this._listeners)[event] || (_this$_listeners[event] = [])).push(fn);
    return this;
  }
  off(event, fn) {
    var list = this._listeners[event];
    if (!list) return this;
    this._listeners[event] = list.filter(f => f !== fn);
    return this;
  }
  _emit(event) {
    for (var _len = arguments.length, args = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
      args[_key - 1] = arguments[_key];
    }
    var list = this._listeners[event];
    if (list) list.forEach(fn => fn(...args));
  }

  // -- State mutator shortcuts --------------------------------------------

  toggleRefinement(attr, value) {
    this.state.toggleRefinement(attr, value);
  }
  clearRefinement(attr, value) {
    this.state.clearRefinement(attr, value);
  }
  setRefinementValues(attr, values) {
    this.state.setRefinementValues(attr, values);
  }
  setNumericRange(attr, range) {
    this.state.setNumericRange(attr, range);
  }
  clearNumericRange(attr) {
    this.state.clearNumericRange(attr);
  }
  setSort(indexName) {
    this.state.setSort(indexName);
  }
  setPage(n) {
    this.state.setPage(n);
  }
  setQuery(q) {
    this.state.setQuery(q);
  }
  clearAll() {
    this.state.clearAll();
  }

  // -- Search execution ---------------------------------------------------

  _debouncedSearch() {
    clearTimeout(this._debounceTimer);
    this._debounceTimer = setTimeout(() => this.search(), SEARCH_DEBOUNCE_MS);
  }
  search() {
    var _this = this;
    return _asyncToGenerator(function* () {
      clearTimeout(_this._debounceTimer);
      var snap = _this.state.snapshot();
      var params = buildSearchParams(snap, _this._searchConfig);
      try {
        var results = yield executeSearch(_this._client, params);
        _this._emit('results', {
          state: snap,
          hits: results.hits,
          facets: results.facets,
          nbHits: results.nbHits,
          nbPages: results.nbPages,
          page: results.page,
          separateCardHits: results.separateCardHits
        });
      } catch (err) {
        console.error('[Algolia PLP] Search failed', err);
        _this._emit('error', err);
      }
    })();
  }
}

/**
 * @param {ConstructorParameters<typeof AlgoliaPlp>[0]} opts
 * @returns {AlgoliaPlp}
 */
export function createAlgoliaPlp(opts) {
  return new AlgoliaPlp(opts);
}